import React, { useEffect, useRef, useState } from 'react';
import { fmtCNPJ, fmtDate } from '../../utils/formatters';
import { cobraAcimaDoContratado, linhaInclusa, quantidadeFaturada } from '../../utils/propostaItens';
import { ehLinhaDerivada } from '../../utils/inclusosDoPacote';
import { tabelaDaMeta, janela24hEhGratis, JANELA_24H_PASSA_A_COBRAR_EM, fraseDaFonte } from '../../config/whatsappMeta';
import './proposta-v17.css';

/**
 * As três fontes da v1.7. Entram por <link> injetado, e não por @import no CSS,
 * porque o CSS do HUB é empacotado num arquivo só: um @import aqui viraria
 * download de fonte em TODA tela do sistema, não só na proposta pública.
 */
const FONTES = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500;700&display=swap';

function useFontesDaProposta() {
  useEffect(() => {
    if (document.querySelector(`link[href="${FONTES}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONTES;
    document.head.appendChild(link);
  }, []);
}

/**
 * PROPOSTA PADRÃO em quatro folhas A4, na linguagem visual da v1.7.
 *
 * Por que existe: a proposta padrão é a via RÁPIDA (o cliente que não lê
 * proposta, o caso simples que precisa sair hoje), e a gerada por briefing é a
 * via completa. As duas moram no mesmo link público — `findByCodePublic` dá
 * precedência ao `customHtmlS3Key` — e até aqui só a segunda tinha desenho.
 * Vinham de mundos diferentes, e a padrão parecia de outra empresa.
 *
 * O que é DADO e o que é FIXO:
 *  · dado — cliente, código, validade, itens, quantidades, unitários, escada de
 *    preço, totais, parcelas da ativação, forma de pagamento, observações. Muda
 *    a quantidade na proposta, muda aqui (inclusive a manchete da capa e a
 *    faixa acesa na escada, que o backend recalcula a cada abertura).
 *  · fixo — o que a IPCOM entrega e os números institucionais. É a parte que
 *    não depende do cliente; quando mudar, muda para todo mundo.
 */

const ANO_FUNDACAO = 2007;

/** "1234.5" → "1.234,50". Sempre sobre o NÚMERO, nunca sobre a frase pronta. */
const brl = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const soma = (xs: any[], f: (x: any) => number) => xs.reduce((t, x) => t + (f(x) || 0), 0);

const codigoDe = (i: any) => String(i?.productCode || '').toUpperCase();
const qtd = (i: any) => Math.max(0, Math.floor(Number(i?.quantity) || 0));

/**
 * Quantos canais simultâneos UMA unidade do produto entrega.
 *
 * Canal NÃO é a quantidade da linha: o produto é a franquia, então duas
 * franquias de 5 são dez canais. Sem isso a capa diria "2 canais" para quem
 * contratou dez. Os dois jeitos de escrever convivem na base de produtos:
 * `STFC-SIP-05CH` (o antigo) e `HUB-CANAIS-05` (o atual).
 */
const canaisDoCodigo = (codigo: string) => {
  const m = codigo.match(/(\d+)\s*CH\b/) || codigo.match(/CANAIS?[-_]?(\d+)/);
  return m ? Number(m[1]) : 0;
};

/**
 * Conta ramais, canais simultâneos e números da proposta para a manchete.
 *
 * ⚠️ Casa pelo CÓDIGO do produto, não pelo nome, e os códigos mudaram de
 * família: hoje é `HUB-RAMAL` / `HUB-CANAIS-05` / `HUB-NUMERO`, e propostas
 * antigas trazem `CLOUD-RAMAL` / `STFC-SIP-05CH` / `STFC-DDR`. Produto que não
 * casa com nenhum padrão simplesmente não entra na frase — a manchete some,
 * a proposta continua de pé.
 */
/**
 * AS DIMENSÕES SÃO INDEPENDENTES, e cada uma conta só o próprio produto.
 *
 * Número de voz e licença de WhatsApp não são duas pontas da mesma coisa.
 * `HUB-WPP-NUMERO` não é um número: é a LICENÇA MENSAL que habilita um número
 * do cliente na API Oficial do WhatsApp. Custo recorrente por número habilitado,
 * e sem custo de ativação — o número é o mesmo, a licença é um serviço sobre ele.
 * Por isso é item separado: o cliente porta 7 DDRs e pode licenciar só 2 na
 * Meta, deixando os outros só na voz.
 *
 * ⚠️ NÃO chame isso de "ativação" — é o engano que esta correção desfez, e a
 * palavra tem outro dono neste arquivo: `HUB-SETUP`, a ativação única e
 * parcelada, que aparece no bloco do investimento e na tabela de P&S.
 *
 * O contador antigo somava os dois, porque `/NUMERO/` casa com HUB-WPP-NUMERO:
 * a capa da PRO20260087 dizia "14 números oficiais" onde havia 7. E só acertaria
 * por acidente, quando uma das dimensões fosse zero — 7 DDR com 2 licenças
 * daria 9, errado de outro jeito.
 *
 * ⚠️ O casamento é sempre por CÓDIGO, nunca por nome: o produto 115 foi
 * renomeado no cadastro e a contagem não sentiu, que é o comportamento certo.
 */
const ehNumeroDeWhatsApp = (i: any) => /WPP/.test(codigoDe(i)) && /NUMERO/.test(codigoDe(i));
const ehNumeroDeVoz = (i: any) => /DDR|NUMERO/.test(codigoDe(i)) && !/WPP/.test(codigoDe(i));

/** "a, b e c" — sem parte vazia no meio. */
const fraseDePartes = (partes: string[]) =>
  partes.length > 1
    ? `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}`
    : partes[0] || '';

export function resumoDeVoz(items: any[]) {
  const ramais = soma(items.filter((i) => /RAMAL/.test(codigoDe(i))), qtd);
  const numeros = soma(items.filter(ehNumeroDeVoz), qtd);
  const canais = soma(items, (i) => canaisDoCodigo(codigoDe(i)) * qtd(i));
  const partes: string[] = [];
  if (ramais > 0) partes.push(`${ramais} ${ramais === 1 ? 'ramal' : 'ramais'}`);
  if (canais > 0) partes.push(`${canais} ${canais === 1 ? 'canal' : 'canais'}`);
  if (numeros > 0) partes.push(`${numeros} ${numeros === 1 ? 'número' : 'números'}`);
  return { ramais, canais, numeros, frase: fraseDePartes(partes) };
}

/**
 * VARIANTE DO DOCUMENTO.
 *
 * O documento inteiro é escrito para telefonia: a capa anuncia central em
 * nuvem, os seis cards falam de ramal, softphone e gravação, e o WhatsApp
 * aparece num rodapé, como se fosse acessório. Para um cliente que está
 * comprando WhatsApp, a primeira coisa que ele lê é o produto errado.
 *
 * A variante troca só o que é DISCURSO — manchete, subtítulo e os seis cards.
 * Valores, composição, escada e totais são os mesmos: eles saem dos itens da
 * proposta, e não têm versão.
 *
 * ⚠️ A variante é escolhida POR PROPOSTA, nunca deduzida do carrinho. Deduzir
 * ("tem combo, logo é WhatsApp") parece esperto e erra no primeiro cliente que
 * compra os dois — que é exatamente o caso de PRO20260085, com 50 combos E 10
 * canais de voz.
 */
export type VarianteDoDocumento = 'voz' | 'whatsapp';

/**
 * Conta atendentes do Combo para a manchete da variante.
 *
 * Casa pelo CÓDIGO, como o `resumoDeVoz` ao lado, e pela mesma razão: nome de
 * item é texto livre que o vendedor edita. Em PRO20260085 os códigos são
 * `HUB-COMBO`, `HUB-CANAIS-10` e `HUB-SETUP`.
 *
 * Produto que não casa não entra na frase, e a manchete cai no texto genérico —
 * a proposta continua de pé. Isso é melhor que contar por semelhança de nome e
 * anunciar "51 atendentes" porque uma linha se chamava "WhatsApp" alguma coisa.
 */
export function resumoDeWhatsApp(items: any[]) {
  const atendentes = soma(items.filter((i) => /COMBO/.test(codigoDe(i))), qtd);
  const numerosNoWhatsApp = soma(items.filter(ehNumeroDeWhatsApp), qtd);
  const { numeros: numerosDeVoz } = resumoDeVoz(items);

  const partes: string[] = [];
  if (atendentes > 0) partes.push(`${atendentes} ${atendentes === 1 ? 'atendente' : 'atendentes'}`);
  // Sem licença, a parte SOME. A versão anterior tinha um fallback que escrevia
  // "um número oficial" mesmo com a contagem em zero — herança de quando o
  // número vinha embutido no Combo. Hoje a licença inclusa é linha explícita no
  // cadastro (HUB-WPP-NUMERO qtd 1 a R$ 0, já dentro do HUB-START-WA), então
  // afirmar um número que ninguém licenciou é o documento prometendo Meta onde
  // não há.
  if (numerosNoWhatsApp > 0) {
    partes.push(`${numerosNoWhatsApp} ${numerosNoWhatsApp === 1 ? 'número' : 'números'} no WhatsApp Oficial`);
  }
  // A proposta pode ter os dois serviços, e aí a manchete cita os dois
  // contadores: são dimensões diferentes, e omitir a de voz esconderia metade
  // do que o cliente está comprando.
  if (numerosDeVoz > 0) {
    partes.push(`${numerosDeVoz} ${numerosDeVoz === 1 ? 'número' : 'números'} de voz`);
  }
  return { atendentes, numerosNoWhatsApp, numerosDeVoz, frase: fraseDePartes(partes) };
}

/**
 * O APP, nas duas lojas.
 *
 * ⚠️ Os nomes DIVERGEM: na App Store a ficha já é "IPCOM Hub", e na Google Play
 * ainda é "IPCOM Softphone" (o pacote é `br.com.ipcom.softphone`, de quando o
 * app se chamava assim). Por isso o texto não manda procurar pelo nome — manda
 * pelo link. Mandar buscar "IPCOM Hub" faria quem tem Android não achar.
 */
const APP_IOS = 'https://apps.apple.com/br/app/ipcom-hub/id6761440015';
const APP_ANDROID = 'https://play.google.com/store/apps/details?id=br.com.ipcom.softphone';

/**
 * O QUE ESTA PROPOSTA TRAZ — não qual documento mostrar.
 *
 * ⚠️ Distinção que vale ler antes de mexer: a VARIANTE do documento nunca é
 * deduzida dos itens (é escolha do vendedor, gravada em `templateVariant`).
 * Isto aqui é outra coisa: descrever o que está no carrinho, que é leitura
 * legítima e a única forma de o fecho não mentir.
 *
 * O parágrafo final era texto fixo e condicional ao mesmo tempo: dizia "quando
 * quiser telefonia, ela entra pelos Canais de Voz" numa proposta que JÁ tem
 * HUB-CANAIS-05 (a PRO20260087), e o espelho acontecia na variante de voz,
 * oferecendo o Combo a quem já comprou o Combo. Oferecer ao cliente o que ele
 * acabou de contratar é o tipo de erro que faz duvidar do resto do documento.
 */
export function oQueAPropostaTraz(items: any[]): 'ambos' | 'whatsapp' | 'voz' {
  const cod = (i: any) => codigoDe(i);
  const temWhatsApp = items.some((i) => /COMBO|START-WA/.test(cod(i)) || ehNumeroDeWhatsApp(i));
  const temVoz = items.some(
    (i) => /RAMAL|START-VOZ/.test(cod(i)) || ehNumeroDeVoz(i) || canaisDoCodigo(cod(i)) > 0,
  );
  if (temWhatsApp && temVoz) return 'ambos';
  return temWhatsApp ? 'whatsapp' : 'voz';
}

type Card = { k: string; h: string; p: string; novo?: boolean };
type PerfilDoDocumento = {
  kicker: string;
  manchete: React.ReactNode;
  /**
   * O resumo do que está sendo contratado, na língua da variante. Usado na
   * manchete E no bloco do investimento — antes o bloco chamava `resumoDeVoz`
   * direto, sem olhar a variante, e uma proposta de WhatsApp dizia "Mensalidade
   * de 5 canais e 14 números", escondendo os atendentes, que são o dado
   * principal dela. Uma fonte só para os dois lugares.
   */
  frase: string;
  sub: string;
  cards: Card[];
  /** Uma linha, não um card: o app não é mais uma funcionalidade entre seis, é
   *  onde a equipe trabalha quando sai da mesa. */
  app: React.ReactNode;
  notaFinal: React.ReactNode;
};

/**
 * O PARÁGRAFO DE FECHO, que agora olha o carrinho antes de falar.
 *
 * É o mesmo texto nas duas variantes de propósito: ele não descreve o documento,
 * descreve a proposta. Uma proposta com os dois serviços fecha do mesmo jeito,
 * seja qual for a capa que o vendedor escolheu.
 */
function notaDoFecho(items: any[]): React.ReactNode {
  const traz = oQueAPropostaTraz(items);
  if (traz === 'ambos') {
    return (
      <>
        <b>Os dois já estão aqui.</b> WhatsApp Oficial e telefonia na mesma plataforma, no mesmo painel e
        na mesma mensalidade — a conversa e a ligação do mesmo cliente ficam lado a lado, com o chat da
        equipe incluso.
      </>
    );
  }
  if (traz === 'whatsapp') {
    return (
      <>
        <b>Quando quiser telefonia</b>, ela entra pelos Canais de Voz, com ramal no navegador, no
        computador e no celular, somando ligação e gravação ao que já está aqui. O chat da equipe não
        muda: continua incluso.
      </>
    );
  }
  return (
    <>
      <b>Quando quiser WhatsApp Oficial da empresa</b>, ele entra pelo Combo, por usuário, somando fila
      e histórico de mensagem ao que já está aqui. O chat da equipe não muda: continua incluso.
    </>
  );
}

/**
 * O texto de cada variante, reunido num lugar só para a diferença ser legível de
 * uma vez — e para ninguém precisar caçar `if` espalhado por quatro páginas.
 */
function perfilDoDocumento(variante: VarianteDoDocumento, items: any[]): PerfilDoDocumento {
  if (variante === 'whatsapp') {
    const { frase } = resumoDeWhatsApp(items);
    return {
      kicker: 'WhatsApp oficial para empresas',
      frase,
      manchete: (
        <>
          {frase ? `${frase}. ` : 'Seu WhatsApp oficial. '}
          <span className="hl">Com fila, histórico e dono de cada conversa.</span>
        </>
      ),
      sub:
        'Um número oficial verificado e a equipe inteira atendendo, pela API oficial do WhatsApp Business. ' +
        'Quando o colaborador sai, o cliente e a conversa ficam com a empresa. A plataforma roda na AWS São Paulo.',
      cards: [
        // ⚠️ O que a IPCOM garante é a conta comercial oficial e o número
        // verificado. O SELO VISUAL de conta verificada é decisão da Meta, pelos
        // critérios dela — prometer o selo é prometer o que não é nosso.
        { k: 'Número', h: 'Número oficial da empresa, verificado na Meta', p: 'WhatsApp Business API oficial, no número da empresa e verificado junto à Meta. A equipe inteira atende pelo mesmo número, sem celular pessoal no meio e sem aparelho fazendo de central. O selo visual de conta verificada é decisão da própria Meta: a IPCOM prepara e submete o pedido.' },
        { k: 'Atendimento', h: 'Cada conversa tem dono, fila e histórico', p: 'A conversa entra na fila com cronômetro visível e é assumida em um clique. Quem está atendendo aparece para o resto da equipe, e o histórico daquele cliente vem junto — sem "quem falou com ele da última vez?".' },
        { k: 'Registro', h: 'A conversa fica com a empresa', p: 'Quando o colaborador sai, o cliente e a conversa continuam na empresa. O histórico é da operação, não do aparelho de quem atendeu, e segue pesquisável por número e por data.' },
        { k: 'Automação', h: 'Agente de IA que responde antes da fila', p: 'O atendente virtual responde na hora, qualifica e resolve o que é repetitivo, e passa para a fila humana o que precisa de gente. A equipe recebe a conversa já com contexto.', novo: true },
        { k: 'Equipe', h: 'Chat interno já incluso', p: 'A conversa da equipe acontece dentro do próprio Hub, sem grupo improvisado em aplicativo pessoal. Já vem no valor, sem cobrança à parte.' },
        { k: 'Infraestrutura', h: 'Nada de servidor na sua empresa', p: 'A plataforma roda em instância dedicada na AWS São Paulo, com 99,95% de disponibilidade em SLA, backup diário e dados em armazenamento seguro. Sem equipamento, nobreak ou manutenção do seu lado.' },
      ],
      app: (
        <>
          <b>No bolso da equipe.</b> O IPCOM Hub é app nativo de iPhone e Android, com a mesma fila,
          o mesmo histórico e o mesmo número do computador. Login e pronto, sem configurar nada.
        </>
      ),
      notaFinal: notaDoFecho(items),
    };
  }

  const { frase } = resumoDeVoz(items);
  return {
    kicker: 'Telefonia em nuvem para empresas',
    frase,
    manchete: (
      <>
        {frase ? `${frase}. ` : 'Sua telefonia em nuvem. '}
        <span className="hl">Numa central só, sem servidor na sua empresa.</span>
      </>
    ),
    sub:
      'Ramais no navegador, no celular e no aparelho da mesa, com fila, gravação e o chat da equipe incluso. ' +
      'A central roda na AWS São Paulo, e a IPCOM é operadora autorizada pela Anatel.',
    cards: [
      { k: 'No navegador', h: 'Ramal sem instalar nada', p: 'O WebPhone abre no Chrome, Edge ou Safari e vira um ramal completo: atender, transferir com espera, pausar, DND e teclado. Um semáforo de cores mostra o estado de cada chamada, e o áudio trafega criptografado.' },
      { k: 'No computador', h: 'Softphone para quem vive no telefone', p: 'O IPCOM Phone roda em Windows e macOS com fila em tempo real, pausa com motivo, presença dos ramais e estatística do agente. Reconecta sozinho quando a rede oscila, com até dez tentativas.' },
      { k: 'No celular', h: 'O mesmo ramal fora do escritório', p: 'O app entrega o ramal com o mesmo número e o mesmo histórico. Trabalhar de casa ou em campo deixa de ser assunto de chamado técnico.' },
      { k: 'Registro', h: 'Gravação que fica com a empresa', p: 'Toda chamada é gravada e registrada, com busca por número e por data. O histórico pertence à empresa, não a quem atendeu, e continua disponível quando a pessoa sai.' },
      { k: 'Equipe', h: 'Chat interno já incluso', p: 'A conversa da equipe acontece dentro do próprio Hub, sem grupo improvisado em aplicativo pessoal. Já vem no valor do ramal, sem cobrança à parte.' },
      { k: 'Infraestrutura', h: 'Nada de central na sua empresa', p: 'A plataforma roda em instância dedicada na AWS São Paulo, com 99,95% de disponibilidade em SLA, backup diário e gravações em armazenamento seguro. Sem equipamento, nobreak ou manutenção do seu lado.' },
    ],
    app: (
      <>
        <b>No bolso da equipe.</b> O IPCOM Hub é app nativo de iPhone e Android, e leva o ramal, a fila
        e o histórico para fora do escritório. Login e pronto, sem configurar nada.
      </>
    ),
    notaFinal: notaDoFecho(items),
  };
}

/**
 * Nome curto para o rótulo da escada, que é mono, caixa alta e de uma linha.
 * "Ramal de Voz IPCOM HUB (só-voz ou ramal avulso)" ocupava a faixa inteira e
 * empurrava a frase do preço para baixo; o parêntese é sempre a ressalva, não
 * a identificação.
 */
const rotuloCurto = (nome: string) => {
  const semParenteses = String(nome || '').split('(')[0].trim();
  return semParenteses.length > 34 ? `${semParenteses.slice(0, 33).trim()}…` : semParenteses;
};

export type DescontoDaLinha = {
  /**
   * Percentual que FECHA com os outros dois números da linha:
   * unitário × quantidade × (1 − pct) = subtotal.
   * `null` quando o desconto da linha é só de volume — ele já está dentro do
   * unitário, e imprimir um número ali quebraria a conta.
   */
  pct: number | null;
  rotulo: string;
  detalhe: string;
};

/**
 * O DESCONTO QUE A COLUNA DEVE MOSTRAR.
 *
 * A regra desta coluna é uma só: **os três números da linha têm que fechar na
 * calculadora do cliente.** Unitário, desconto e subtotal são lidos juntos, e
 * quem confere é quem vai assinar.
 *
 * Duas versões erraram aqui, por motivos opostos.
 *
 * A primeira lia só `item.discount`, o desconto MANUAL do vendedor. Com desconto
 * por volume aplicado, a coluna mostrava traço: o cliente lia "Desconto: –" e
 * concluía que não ganhou nada, enquanto três centímetros abaixo a escada dizia
 * 20%. A tabela se contradizia sozinha.
 *
 * A segunda, consertando isso, passou a compor volume com manual. Aí a conta
 * parou de fechar, porque a coluna do lado mostra o unitário JÁ com o volume
 * aplicado: em PRO20260085 saíam R$ 40,00 de unitário (faixa 31–50), −38,18% de
 * desconto e R$ 1.700,00 de subtotal, e 40 × (1 − 0,3818) × 50 dá 1.236, não
 * 1.700. Os 38,18% eram contra os R$ 55,00 da primeira faixa — base diferente da
 * do unitário impresso ao lado.
 *
 * Agora as duas coisas ficam separadas, que é como o cliente enxerga melhor:
 *
 * - **desconto comercial** (o negociado) é o que vai na coluna, sobre o unitário
 *   que está ali do lado. 15% sobre R$ 40,00 fecha: 40 × 0,85 × 50 = 1.700.
 * - **desconto por volume** não vira número nesta coluna. Ele já está no
 *   unitário, e quem o explica é a escada logo abaixo, que mostra o 55 virando
 *   40 sozinha. Na coluna fica só a remissão, para a linha não voltar a dizer
 *   "não ganhou nada".
 */
export function descontoEfetivo(item: any): DescontoDaLinha | null {
  const manual = Number(item?.discount) || 0;
  const volume = Number(item?.escada?.descontoPct) || 0;
  if (manual <= 0 && volume <= 0) return null;

  if (manual > 0) {
    return {
      pct: manual,
      rotulo: `-${brl(manual)}%`,
      detalhe:
        volume > 0
          ? `${brl(manual)}% negociado sobre o unitário de R$ ${brl(item?.unitPrice)}, que já é o preço da sua faixa de volume`
          : `${brl(manual)}% negociado`,
    };
  }

  return {
    pct: null,
    rotulo: 'por volume',
    detalhe: `O unitário ao lado já é o preço da sua faixa, ${brl(volume)}% abaixo da primeira. A escada abaixo abre a conta.`,
  };
}

/**
 * ESCADA DE PREÇO POR VOLUME — o desconto que o cliente já ganhou e o próximo
 * degrau. O backend só manda `item.escada` quando ela EXPLICA o preço cobrado
 * ao lado (`helpers/faixas-proposta.util.ts`); aqui é só desenho.
 */
/**
 * O cliente já está na primeira faixa?
 *
 * Aí o unitário dele É o preço cheio, e a frase "na sua quantidade o unitário é
 * R$ 25,00, contra R$ 25,00 da primeira faixa" compara o preço com ele mesmo —
 * acontece na PRO20260086, no item HUB-NUMERO com 6 unidades. Quem lê isso numa
 * proposta ou acha que é erro de impressão, ou para para entender e não entende.
 *
 * Tolerância em vez de igualdade exata porque os dois valores chegam da API como
 * decimal, às vezes string, e 25 === 25.000000001 não é a pergunta que interessa.
 */
export function estaNaPrimeiraFaixa(escada: any): boolean {
  const atual = (escada?.faixas || []).find((f: any) => f.atual);
  const cheio = Number(escada?.precoCheio);
  const unit = Number(atual?.unitPrice);
  if (!Number.isFinite(cheio) || !Number.isFinite(unit)) return false;
  return Math.abs(unit - cheio) < 0.005;
}

/**
 * ESCADA COM O DESCONTO NEGOCIADO JÁ APLICADO (decisão do Heitor, 30/09/2026).
 *
 * O defeito era na PRO20260089 (BIS2BIS): 8 DDR a R$ 25,00 com 30% negociado,
 * a cliente paga R$ 17,50, e a escada crua anunciava "cai para R$ 22,00" —
 * 25,7% MAIS CARO do que ela já paga. Desde o backend #262 as faixas e o
 * `precoCheio` chegam multiplicados por (1 − desconto), e `descontoNegociadoPct`
 * diz quanto.
 *
 * ⛔ O risco que sobra é aqui: a coluna "Valor unit." da mesma linha continua
 * em R$ 25,00 e a escada passa a dizer R$ 17,50. Dois unitários na mesma linha
 * sem explicação é o terceiro descompasso de base deste arquivo (os outros dois
 * estão no comentário de `descontoEfetivo`). Por isso, com desconto, a escada
 * DIZ que está com ele, no rótulo e em cada frase, e amarra o número dela ao da
 * coluna: "R$ 17,50 (R$ 25,00 − 30%)". De quebra a conta fecha na calculadora
 * da cliente: 8 × 17,50 = 140,00, o subtotal impresso.
 *
 * Sem desconto (0, ou campo ausente em resposta antiga) o HTML sai IDÊNTICO ao
 * de antes; o teste compara byte a byte. Desconto de 100% nem chega aqui: o
 * backend não manda escada.
 */
const descontoDaEscada = (escada: any): number => {
  const d = Number(escada?.descontoNegociadoPct) || 0;
  return d > 0 && d < 100 ? d : 0;
};

export const Escada: React.FC<{ escada: any; rotuloItem: string; unitarioDaTabela?: any }> = ({ escada, rotuloItem, unitarioDaTabela }) => {
  const faixas: any[] = escada?.faixas || [];
  if (faixas.length < 2) return null;
  const atual = faixas.find((f) => f.atual);
  const d = descontoDaEscada(escada);
  // A remissão à coluna só entra quando a conta FECHA: unitário da tabela menos
  // o desconto tem de dar o degrau atual. Se não fechar (faturada acima do
  // contratado, dado antigo), melhor omitir que imprimir conta errada.
  const daTabela = Number(unitarioDaTabela);
  const fecha = d > 0 && Number.isFinite(daTabela) && daTabela > 0
    && Math.abs(daTabela * (1 - d / 100) - Number(atual?.unitPrice)) < 0.01;
  const remissao = fecha ? <> (R$ {brl(daTabela)} − {brl(d)}%)</> : null;
  return (
    <tr className="lad">
      <td colSpan={5}>
        <div className="lad-box">
          <div className="lad-hd">
            <span>
              <span className="lab">Preço por volume · {rotuloItem}{d > 0 && <> · já com os {brl(d)}% de desconto</>}</span>
              {/* Na primeira faixa a comparação some: ela diria o mesmo número
                  duas vezes. O que sobra é o unitário, e o degrau seguinte —
                  que é a informação útil para quem ainda não desceu nenhum. */}
              {d > 0 ? (
                estaNaPrimeiraFaixa(escada) ? (
                  <>&nbsp;com o desconto, o unitário na sua quantidade é <b>R$ {brl(atual?.unitPrice)}</b>{remissao}</>
                ) : (
                  <>&nbsp;com o desconto, na sua quantidade o unitário é <b>R$ {brl(atual?.unitPrice)}</b>{remissao}, contra R$ {brl(escada.precoCheio)} da primeira faixa</>
                )
              ) : estaNaPrimeiraFaixa(escada) ? (
                <>&nbsp;o unitário na sua quantidade é <b>R$ {brl(atual?.unitPrice)}</b></>
              ) : (
                <>&nbsp;na sua quantidade o unitário é <b>R$ {brl(atual?.unitPrice)}</b>, contra R$ {brl(escada.precoCheio)} da primeira faixa</>
              )}
            </span>
            {escada.faltamParaProximo > 0 && escada.proximoUnitario != null && (
              <em>
                Com mais <b>{escada.faltamParaProximo} {escada.faltamParaProximo === 1 ? 'unidade' : 'unidades'}</b>, o unitário{d > 0 && ' com desconto'} cai para <b>R$ {brl(escada.proximoUnitario)}</b>
              </em>
            )}
          </div>
          <div className="lad-g">
            {faixas.map((f, i) => {
              // O rótulo sai do degrau SEGUINTE: a última faixa é aberta e as
              // do meio são intervalos fechados.
              const prox = faixas[i + 1];
              const rotulo = !prox
                ? `${f.minQuantity}+`
                : f.minQuantity === 1
                  ? `até ${prox.minQuantity - 1}`
                  : `${f.minQuantity}–${prox.minQuantity - 1}`;
              return (
                <span key={f.minQuantity} className={f.atual ? 'c on' : 'c'}>
                  <span className="r">{rotulo}</span>
                  <span className="p">{brl(f.unitPrice)}</span>
                </span>
              );
            })}
          </div>
        </div>
      </td>
    </tr>
  );
};

/**
 * DESCRIÇÃO DO ITEM, com o resto atrás de um clique.
 *
 * O texto vem do CADASTRO do produto e costuma ser ficha técnica inteira — a
 * maior em produção tem 1.482 caracteres (HUB-START-WA), e cinco produtos
 * passam de 690. Por isso o corte em duas linhas existe e continua existindo:
 * sem ele, uma composição de cinco ou seis itens vira parede de texto e some o
 * que o cliente veio ler, que são os valores.
 *
 * O defeito não era o corte, era não haver saída dele: o resto do texto não
 * existia em lugar nenhum da tela. Agora existe, sem custar a leitura de quem
 * só quer conferir preço.
 *
 * O botão só aparece quando há mesmo texto escondido, e isso é MEDIDO, não
 * adivinhado: o corte é por LINHA, então depende da largura da coluna, da fonte
 * e do texto. Contar caractere erraria nos dois sentidos — ofereceria "ver mais"
 * onde não há nada a ver, e esconderia onde há.
 *
 * Na impressão o corte sai inteiro (ver o @media print do CSS): papel não tem
 * clique, e o PDF que o backend gera para o cliente já imprime tudo. Quem
 * imprime esta tela merece o mesmo documento.
 */
const DescricaoDoItem: React.FC<{ texto: string }> = ({ texto }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [aberta, setAberta] = useState(false);
  const [cortada, setCortada] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => {
      // Aberta, scrollHeight e clientHeight se igualam e a medição diria "não
      // está cortada" — o que apagaria o botão de fechar. Só mede fechada.
      if (aberta) return;
      setCortada(el.scrollHeight > el.clientHeight + 1);
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [texto, aberta]);

  return (
    <>
      <div ref={ref} className={aberta ? 'sub2 aberta' : 'sub2'}>{texto}</div>
      {(cortada || aberta) && (
        <button type="button" className="vermais" onClick={() => setAberta(v => !v)} aria-expanded={aberta}>
          {aberta ? 'ver menos' : 'ver descrição completa'}
        </button>
      )}
    </>
  );
};

/**
 * O QUE JÁ VEM NO PACOTE SAI DE DENTRO DA LINHA DO PACOTE.
 *
 * A linha derivada era um <tr> completo, com Qtd., "Incluso" e as mesmas
 * colunas de um item cobrado, e ainda repetia o que o nome do pacote já dizia
 * ("Plataforma + 10 ramais" e, embaixo, "10 · Ramal de Voz (inclusos no
 * pacote)"). Com o mesmo peso visual de cobrança, o cliente parou a negociação
 * para perguntar o que estava dentro dos R$ 400 (BIS2BIS, 30/09/2026). Agora
 * ela vira uma faixa sob a descrição do pai, sem coluna de preço.
 *
 * ⛔ O critério é a MARCA (`ehLinhaDerivada`), nunca o preço zero
 * (`linhaInclusa`). Linha zerada sem marca é cortesia que o vendedor zerou à
 * mão ou proposta antiga da convenção manual: não tem pai onde se pendurar e
 * SUMIRIA do documento. Essas seguem como linha própria com "Incluso" (regra 5
 * do `inclusosDoPacote.ts`). Derivada cujo pai não está nesta tabela também
 * fica como linha: esconder o que o cliente leva é pior que uma linha a mais.
 *
 * O pai é a ÚLTIMA linha do pacote antes da derivada, que é onde o
 * `sincronizarInclusos` a ancora. Se o vendedor arrastou a derivada para cima
 * do pacote, vale a última linha do pacote na tabela.
 */
export interface LinhaComInclusos {
  item: any;
  inclusos: any[];
}

export function agruparInclusos(itens: any[]): LinhaComInclusos[] {
  const lista = Array.isArray(itens) ? itens : [];
  const paiDe = (derivada: any, idx: number): number => {
    const pacoteId = Number(derivada?.includedByProductId);
    const ehPai = (i: any) => !ehLinhaDerivada(i) && Number(i?.productId) === pacoteId;
    for (let j = idx - 1; j >= 0; j--) if (ehPai(lista[j])) return j;
    for (let j = lista.length - 1; j > idx; j--) if (ehPai(lista[j])) return j;
    return -1;
  };

  const penduradas = new Map<number, any[]>();
  const soltas = new Set<number>();
  lista.forEach((item, idx) => {
    if (!ehLinhaDerivada(item)) return;
    const pai = paiDe(item, idx);
    if (pai < 0) return;
    soltas.add(idx);
    const ja = penduradas.get(pai);
    if (ja) ja.push(item);
    else penduradas.set(pai, [item]);
  });

  return lista
    .map((item, idx) => ({ item, inclusos: penduradas.get(idx) || [], idx }))
    .filter(l => !soltas.has(l.idx))
    .map(({ item, inclusos }) => ({ item, inclusos }));
}

/**
 * Como a faixa nomeia cada incluso. Ramal e número têm nome de vendedor no
 * cadastro ("Número fixo direto adicional (DDR)") que, dentro do pacote, diz
 * o contrário do que é: não é adicional. Para esses dois o texto vem do
 * vocabulário do glossário logo abaixo da tabela; o resto usa o nome da linha
 * sem o sufixo "(incluso no pacote)", que a própria faixa já diz.
 */
export function rotuloDoIncluso(item: any): string {
  const q = qtd(item);
  const codigo = codigoDe(item);
  if (codigo === 'HUB-RAMAL') return `${q} ${q === 1 ? 'ramal' : 'ramais'}`;
  if (codigo === 'HUB-NUMERO') return `${q} ${q === 1 ? 'número fixo (DDR)' : 'números fixos (DDR)'}`;
  const nome = String(item?.name || '').replace(/\s*\(\s*inclus[oa]s?\s+no\s+pacote\s*\)\s*$/i, '').trim();
  return `${q} × ${nome}`;
}

const LinhasDaTabela: React.FC<{ itens: any[] }> = ({ itens }) => (
  <>
    {agruparInclusos(itens).map(({ item, inclusos }, idx: number) => {
      const desc = descontoEfetivo(item);
      return (
        <React.Fragment key={idx}>
          <tr>
            <td className="q">{item.quantity}</td>
            <td>
              <div className="it">{item.name}</div>
              {/* A conta do cliente com papel e caneta TEM que fechar. Com a
                  regra de faixa que vale para todas as unidades, cobrar uma a
                  mais às vezes sai mais barato — e aí quantidade × unitário não
                  dá o subtotal. Calar isso é convidar a ligação. */}
              {cobraAcimaDoContratado(item) && (
                <div className="fat">
                  Cobrado como <b>{quantidadeFaturada(item)}</b> unidades: nesta faixa o preço
                  vale para todas, e contar {quantidadeFaturada(item)} sai <b>mais barato</b> do
                  que contar {item.quantity}. O subtotal ao lado já é esse.
                </div>
              )}
              {item.description && <DescricaoDoItem texto={item.description} />}
              {inclusos.length > 0 && (
                <div className="inclui">
                  Já incluso neste valor: <b>{inclusos.map(rotuloDoIncluso).join(' · ')}</b>
                </div>
              )}
            </td>
            {linhaInclusa(item)
              ? <td className="inc">Incluso</td>
              : <td className="n">R$ {brl(item.unitPrice)}</td>}
            <td className={desc ? (desc.pct == null ? 'd vol' : 'd') : 'd off'} title={desc?.detalhe}>
              {desc ? desc.rotulo : '–'}
            </td>
            {linhaInclusa(item)
              ? <td className="inc">Incluso</td>
              : <td className="s">R$ {brl(item.subtotal)}</td>}
          </tr>
          {item.escada && <Escada escada={item.escada} rotuloItem={rotuloCurto(item.name)} unitarioDaTabela={item.unitPrice} />}
        </React.Fragment>
      );
    })}
  </>
);

const Cabecalho = () => (
  <thead>
    <tr>
      <th>Qtd.</th><th>Item</th><th>Valor unit.</th><th>Desconto</th><th>Subtotal</th>
    </tr>
  </thead>
);

/**
 * A TELA DO DIA A DIA — a âncora visual da página 2.
 *
 * É MARKUP, não imagem: nítido em qualquer zoom, correto no PDF, poucos
 * kilobytes. E é o PRODUTO — render bonito qualquer concorrente gera; a tela do
 * Hub, não. As linhas seguem o que a proposta VENDE.
 *
 * ⛔ TRÊS TENTATIVAS JÁ FORAM REPROVADAS AQUI, e a nota sobrevive ao worktree
 * onde nasceu (`feat/mockup-na-proposta`, parado desde 18/09) porque a terceira
 * é a mais fácil de repetir por engano:
 *
 *   1. trocar o mockup por um diagrama de caixas — simplório;
 *   2. tirar toda imagem e deixar seis parágrafos — conteúdo bom, leitura seca;
 *   3. portar o mockup escuro de então PARA O TEMA CLARO, perdendo o acabamento
 *      inteiro. O Heitor comparou lado a lado com o site e a diferença era
 *      gritante.
 *
 * ⚠️ O que reprovou a terceira NÃO foi a cor, foi o ACABAMENTO que se perdeu
 * junto: avatar com gradiente e selo do canal sobreposto, tag de canal como
 * pílula com borda, "Gravando" com ponto, hora em monoespaçada, e a linha ativa
 * com borda interna (`box-shadow: inset`), não fundo chapado. Tudo isso está
 * preservado aqui e nas regras `.shot` do CSS — quem mexer, mantenha.
 *
 * O claro desta versão é escolha deliberada da linguagem da v1.7, endossada pelo
 * Heitor em 25/09 ao apontar o mockup claro da proposta gerada por IA
 * (PRO20260071) como referência. Não é a tentativa 3 repetida por esquecimento.
 */
const MockupDaCaixa: React.FC<{ variante: VarianteDoDocumento }> = ({ variante }) => {
  const ehWhatsApp = variante === 'whatsapp';
  return (
    <>
      <div className="shot">
        <div className="bar">
          <span className="dots">
            <i style={{ background: '#FF5F57' }} /><i style={{ background: '#FEBC2E' }} /><i style={{ background: '#28C840' }} />
          </span>
          <span className="t">{ehWhatsApp ? 'Atendimento' : 'Caixa unificada'}</span>
          <span className="u"><i style={{ width: 7, height: 7, borderRadius: '50%', background: '#16A34A', display: 'block' }} />Fernanda Costa · {ehWhatsApp ? 'Fiscal' : 'Ramal 1002'}</span>
        </div>
        <div className="hd">
          <span className="ti">Conversas</span>
          <span className="pills">
            <span className="pill on">Todas</span>
            {ehWhatsApp ? <><span className="pill">WhatsApp</span><span className="pill">Voz</span></>
                        : <><span className="pill">Voz</span><span className="pill">Chat</span></>}
          </span>
        </div>

        {ehWhatsApp ? (
          <>
            <div className="row live">
              <span className="av" style={{ background: 'linear-gradient(135deg,#2E7D6B,#1F8A5B)' }}>TR<span className="b" style={{ background: '#25D366' }}><IconeChat /></span></span>
              <span className="mid"><span className="nm"><b>Transportadora Lima</b><span className="chip">Fiscal</span><span className="chip rec">● Com a Fernanda</span></span><span className="ms">Podem me mandar a guia que vence esta semana?</span></span>
              <span className="rt"><span className="hr">02:14</span></span>
            </div>
            <div className="row">
              <span className="av" style={{ background: 'linear-gradient(135deg,#6B4FA5,#4A2F8F)' }}>MM<span className="b" style={{ background: '#25D366' }}><IconeChat /></span></span>
              <span className="mid"><span className="nm"><b>Metalúrgica Moraes</b><span className="chip">Na fila · 00:48</span></span><span className="ms">O banco pediu o balancete atualizado.</span></span>
              <span className="rt"><span className="hr">09:03</span><br /><span className="bdg" style={{ background: '#25D366' }}>2</span></span>
            </div>
            <div className="row">
              <span className="av" style={{ background: 'linear-gradient(135deg,#3A6EA5,#16478F)' }}>CL<span className="b" style={{ background: 'var(--brand)' }}><IconeChat /></span></span>
              <span className="mid"><span className="nm"><b>Clínica Odonto</b><span className="chip">Agente de IA</span></span><span className="ms">Respondido antes da fila: horário de atendimento</span></span>
              <span className="rt"><span className="hr">08:52</span></span>
            </div>
            <div className="row">
              <span className="av" style={{ background: 'linear-gradient(135deg,#A5563A,#8F3A1F)' }}>DI<span className="b" style={{ background: 'var(--brand)' }}><IconeTelefone /></span></span>
              <span className="mid"><span className="nm"><b>Distribuidora Sul</b><span className="chip">Voz</span></span><span className="ms">A telefonia que você já usa, na mesma tela.</span></span>
              <span className="rt"><span className="hr">08:40</span></span>
            </div>
          </>
        ) : (
          <>
            <div className="row live">
              <span className="av" style={{ background: 'linear-gradient(135deg,#3A6EA5,#16478F)' }}>RA<span className="b" style={{ background: 'var(--brand)' }}><IconeTelefone /></span></span>
              <span className="mid"><span className="nm"><b>Ricardo Almeida</b><span className="chip rec">● Gravando</span></span><span className="ms">Em ligação · +55 41 99812-3344</span></span>
              <span className="rt"><span className="hr">02:14</span></span>
            </div>
            <div className="row">
              <span className="av" style={{ background: 'linear-gradient(135deg,#6B4FA5,#4A2F8F)' }}>CO<span className="b" style={{ background: 'var(--info)' }}><IconeChat /></span></span>
              <span className="mid"><span className="nm"><b>Comercial</b><span className="chip">Chat interno</span></span><span className="ms">Fernanda: cliente do Plano 300 fechou!</span></span>
              <span className="rt"><span className="hr">14:25</span><br /><span className="bdg" style={{ background: 'var(--info)' }}>5</span></span>
            </div>
            <div className="row">
              <span className="av" style={{ background: 'linear-gradient(135deg,#A5563A,#8F3A1F)' }}>MS<span className="b" style={{ background: 'var(--brand)' }}><IconeTelefone /></span></span>
              <span className="mid"><span className="nm"><b>Marcos Silva</b><span className="chip">Chamada perdida</span></span><span className="ms">+55 41 3122-5150 · retornar</span></span>
              <span className="rt"><span className="hr">14:18</span></span>
            </div>
            <div className="row">
              <span className="av" style={{ background: 'linear-gradient(135deg,#2E7D6B,#1F8A5B)' }}>SU<span className="b" style={{ background: 'var(--info)' }}><IconeChat /></span></span>
              <span className="mid"><span className="nm"><b>Suporte</b><span className="chip">Chat interno</span></span><span className="ms">Carlos: ramal 1014 reconectado</span></span>
              <span className="rt"><span className="hr">13:57</span></span>
            </div>
          </>
        )}

        <div className="ft">
          <span className="in">{ehWhatsApp ? 'Responder, transferir ou assumir da fila…' : 'Discar, transferir ou responder no chat…'}</span>
          <span className="sb">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1A1206" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>
          </span>
        </div>
      </div>
      <p className="cap">
        Tela ilustrativa. {ehWhatsApp
          ? 'Cada conversa com dono, fila com cronômetro e histórico do cliente — WhatsApp e voz na mesma caixa.'
          : 'Voz e chat da equipe na mesma caixa, no navegador ou no aplicativo.'}
      </p>
    </>
  );
};

/**
 * O total de páginas é PARÂMETRO porque a variante de WhatsApp tem uma a mais —
 * a dos custos da Meta. Fixo em "04", o rodapé anunciaria quatro páginas num
 * documento de cinco, e o cliente conta.
 */
const Rodape: React.FC<{ texto: string; n: number; de: number }> = ({ texto, n, de }) => (
  <div className="foot">
    <span>{texto}</span>
    <span className="f-num">0{n} · 0{de}</span>
  </div>
);

const LogoIpcom = () => (
  <span className="logo">
    <svg width="26" height="26" viewBox="0 0 390 448" aria-hidden="true">
      <path d="M195,426.02 247,396 247,328 304.04,360.93 368,324 368,277 276.2,224 195,270.88 113.8,224 22,277 22,324 85.96,360.93 143,328 143,396Z" fill="#F47A20" />
      <path d="M195,21.98 247,52 247,120 304.04,87.07 368,124 368,171 276.2,224 195,177.12 113.8,224 22,171 22,124 85.96,87.07 143,120 143,52Z" fill="#3E86D6" />
      <path d="M195,177.12 276.2,224 195,270.88 113.8,224Z" fill="#F47A20" />
    </svg>
    ipcom
  </span>
);

const IconeTelefone = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#1A1206" strokeWidth="2.8" strokeLinecap="round">
    <path d="M13.8 16.6a1 1 0 0 0 1.2-.3l.4-.5a2 2 0 0 1 1.6-.8h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.5.4a1 1 0 0 0-.3 1.2 14 14 0 0 0 6.4 6.4" />
  </svg>
);

const IconeChat = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round">
    <path d="M21 12a8 8 0 0 1-8 8H7l-4 3v-4.5A8 8 0 0 1 11 4h2a8 8 0 0 1 8 8z" />
  </svg>
);

const PropostaPadrao: React.FC<{ proposal: any; variante?: VarianteDoDocumento }> = ({ proposal, variante = 'voz' }) => {
  useFontesDaProposta();
  const items: any[] = Array.isArray(proposal.items) ? proposal.items.flat() : [];
  const itensMRR = items.filter((i: any) => i.type === 'MRR');
  const itensPS = items.filter((i: any) => i.type === 'PS' || i.type === 'Serviço' || i.type === 'Servico');

  const totalMRR = parseFloat(proposal.totalMRR) || 0;
  const totalPS = parseFloat(proposal.totalPS) || 0;
  const parcelas = Number(proposal.installments) || 1;
  const contractMonths = Number(proposal.contractMonths) || 12;
  const anos = new Date().getFullYear() - ANO_FUNDACAO;

  // A frase do resumo vem do perfil, que sabe a variante. O componente não
  // chama mais `resumoDeVoz` direto: era isso que fazia a proposta de WhatsApp
  // descrever a mensalidade em canais e números de voz.
  const perfil = perfilDoDocumento(variante, items);
  // A variante de WhatsApp ganha a página de custos da Meta.
  const totalDePaginas = variante === 'whatsapp' ? 5 : 4;
  // A tabela da Meta vem do servidor quando o payload a traz (uma fonte só); o
  // config local é o fallback declarado, para o documento público não ficar sem
  // tabela se ela não vier. Ver `tabelaDaMeta`.
  const meta = tabelaDaMeta((proposal as any)?.metaTarifas);

  // Quanto o desconto por volume vale por mês, somando só os itens em que a
  // escada explica o preço. Sem item com escada, o parágrafo não aparece:
  // número inventado numa proposta é pior que parágrafo a menos.
  const comEscada = itensMRR.filter((i: any) => i.escada?.descontoPct > 0);
  const semDesconto = soma(comEscada, (i: any) => qtd(i) * Number(i.escada.precoCheio));
  const comDesconto = soma(comEscada, (i: any) => Number(i.subtotal) || 0);
  const economia = semDesconto - comDesconto;

  return (
    <div className="pp17">
      {/* ═══ 01 · CAPA ═══ */}
      <div className="page">
        <div className="topbar" />
        <div className="brandrow">
          <LogoIpcom />
          <span>Proposta comercial · {new Date().getFullYear()}</span>
        </div>

        <div className="kicker">{perfil.kicker}</div>
        <h1>{perfil.manchete}</h1>
        <p className="sub">{perfil.sub}</p>

        <div className="client">
          <span className="cl">Preparada para</span>
          <div className="cn">{proposal.clientName}</div>
          <div className="cm">
            {proposal.contactName && <span>A/C: <b>{proposal.contactName}</b></span>}
            {proposal.clientCnpj && <span>CNPJ {fmtCNPJ(proposal.clientCnpj)}</span>}
            <span>Proposta nº <b>{proposal.code}</b></span>
            {proposal.validUntil && <span>Válida até <b>{fmtDate(proposal.validUntil)}</b></span>}
          </div>
        </div>

        <div className="bkick">Quem garante isso · <b>a IPCOM em números</b></div>
        <div className="bento">
          <div className="tile"><span className="v">{anos} anos</span><span className="l">de operação, desde {ANO_FUNDACAO}, com tecnologia própria</span></div>
          <div className="tile"><span className="v">6.000+</span><span className="l">números em operação para clientes de todo o país</span></div>
          <div className="tile hero"><span className="v">150 mil+</span><span className="l">chamadas do SAMU 192 por mês rodam na IPCOM</span></div>
          <div className="tile"><span className="v">99,95%</span><span className="l">de disponibilidade como compromisso de SLA</span></div>
        </div>

        <Rodape texto="IPCOM · Prestadora de STFC autorizada pela Anatel · Cascavel/PR" n={1} de={totalDePaginas} />
      </div>

      {/* ═══ 02 · O INVESTIMENTO ═══ */}
      <div className="page">
        <div className="topbar" />
        <div className="kicker">O investimento</div>
        <h1>Um valor mensal só, <span className="hl">sem fidelidade e sem multa.</span></h1>
        <p className="sub">
          Tudo que está nesta proposta cabe num valor mensal só. A ativação é única e parcelada,
          e não há multa para cancelar.
        </p>

        <div className="invest">
          <div className="big">
            <div className="v">R$ {brl(totalMRR)}<small>/mês</small></div>
            <div className="l">
              {/* `perfil.frase`, não `resumoDeVoz`: numa proposta de WhatsApp este
                  bloco dizia "Mensalidade de 5 canais e 14 números" e escondia os
                  atendentes, que são o dado principal dela. A capa e o preço agora
                  falam a mesma língua porque saem da mesma fonte. */}
              {perfil.frase ? `Mensalidade de ${perfil.frase}, com o chat da equipe incluso.` : 'Mensalidade de tudo que está nesta proposta.'}
            </div>
            <div className="tags">
              <span className="tag ok">Sem fidelidade</span>
              <span className="tag">Pagamento por {proposal.paymentMethod || 'boleto'}</span>
              <span className="tag">Contrato de {contractMonths} meses</span>
            </div>
          </div>
          {totalPS > 0 && (
            <div className="ativ">
              <span className="k3">Ativação única</span>
              <span className="v3">{parcelas}x de R$ {brl(totalPS / parcelas)}</span>
              <span className="l3">
                Total de R$ {brl(totalPS)}. Implantação, configuração de filas, ramais e números,
                portabilidade quando houver, e treinamento da equipe.
              </span>
            </div>
          )}
        </div>
        <div className="bkick">Como a equipe usa · <b>a tela do dia a dia</b></div>
        <MockupDaCaixa variante={variante} />
        <Rodape texto="A composição dos valores segue na próxima página" n={2} de={totalDePaginas} />
      </div>

      {/* ═══ 03 · A COMPOSIÇÃO ═══ */}
      <div className="page">
        <div className="topbar" />
        <div className="kicker">A composição</div>
        <h1>De onde vem <span className="hl">o valor mensal.</span></h1>
        <p className="sub">Cada item com quantidade, valor unitário e o desconto por volume já aplicado. Nada fica implícito.</p>

        {itensMRR.length > 0 && (
          <table className="tbl">
            <Cabecalho />
            <tbody><LinhasDaTabela itens={itensMRR} /></tbody>
          </table>
        )}

        {itensPS.length > 0 && (
          <>
            <div className="bkick">Uma vez só · <b>o que entra na ativação</b></div>
            <table className="tbl" style={{ marginTop: 10 }}>
              <Cabecalho />
              <tbody><LinhasDaTabela itens={itensPS} /></tbody>
            </table>
          </>
        )}

        <div className="tot">
          {totalMRR > 0 && (<><span className="k2">Mensalidade (MRR)</span><span className="v2">R$ {brl(totalMRR)}</span></>)}
          {totalPS > 0 && (<><span className="k2">Ativação única, em {parcelas}x de R$ {brl(totalPS / parcelas)}</span><span className="v2">R$ {brl(totalPS)}</span></>)}
          <span className="k2 grand">{totalPS > 0 ? 'Total do primeiro mês' : 'Total mensal'}</span>
          {/* ⚠️ No primeiro mês entra UMA parcela da ativação, não o total dela.
              Somar os R$ 1.500,00 inteiros num "total do primeiro mês" cobra do
              cliente, no papel, o que ele vai pagar em dez vezes. */}
          <span className="v2 grand">R$ {brl(totalMRR + totalPS / parcelas)}</span>
        </div>

        {/* Esconder a linha repetida não responde o que a BIS2BIS perguntou: a
            dúvida era de VOCABULÁRIO ("sete ramais fica no WhatsApp?"), não de
            layout. Sem isto a pergunta volta, só com menos o que apontar.
            O chat do ramal é o chat INTERNO da equipe na central, não o
            WhatsApp (confirmado pelo Heitor em 30/09/2026): "chat" solto era
            justamente o que a fazia perguntar se os ramais iam para o WhatsApp. */}
        <p className="glossario">
          <b>Ramal</b> é cada pessoa que atende (voz e chat interno da equipe). <b>Canal</b> é quantas ligações
          acontecem ao mesmo tempo. <b>Número</b> é o telefone que o cliente disca.
        </p>

        {economia > 0.01 && (
          <div className="note" style={{ borderLeftColor: 'var(--brand)' }}>
            <b>O desconto por volume já está aplicado acima.</b> Sem ele, os itens com preço por faixa
            somariam R$ {brl(semDesconto)} por mês, um total de R$ {brl(economia)} a mais.
          </div>
        )}

        {proposal.notes && (
          <div className="note">
            <b>Observações.</b> {proposal.notes}
          </div>
        )}

        <Rodape texto="Valores em reais, sem fidelidade. Reajuste anual pelo IPCA." n={3} de={totalDePaginas} />
      </div>

      {/* ═══ 04 · O QUE ESTÁ INCLUSO ═══ */}
      <div className="page">
        <div className="topbar" />
        <div className="kicker">O que está incluso</div>
        <h1>Tudo abaixo <span className="hl">cabe no valor mensal.</span></h1>
        <p className="sub">Sem cobrança à parte, sem módulo escondido e sem equipamento no seu escritório.</p>

        <div className="grid2">
          {perfil.cards.map((c) => (
            <div className="card" key={c.h}>
              <span className="k">{c.k}{c.novo && <b className="novo">novo</b>}</span>
              <h3>{c.h}</h3>
              <p>{c.p}</p>
            </div>
          ))}
        </div>

        <div className="appbar">
          <span className="ic" aria-hidden="true">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#0B2440" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <rect x="6" y="2" width="12" height="20" rx="2.6" /><path d="M11 18.6h2" />
            </svg>
          </span>
          <span className="tx">{perfil.app}</span>
          <span className="lj">
            <a href={APP_IOS} target="_blank" rel="noreferrer">App Store</a>
            <a href={APP_ANDROID} target="_blank" rel="noreferrer">Google Play</a>
          </span>
        </div>

        <div className="note">{perfil.notaFinal}</div>

        <Rodape texto="IPCOM · Prestadora de STFC autorizada pela Anatel · Cascavel/PR" n={4} de={totalDePaginas} />
      </div>

      {/* ═══ 05 · TRANSPARÊNCIA (só na variante de WhatsApp) ═══ */}
      {variante === 'whatsapp' && (
        <div className="page">
          <div className="topbar" />
          <div className="kicker">Transparência</div>
          <h1>As duas contas à parte, <span className="hl">sem surpresa na fatura.</span></h1>
          <p className="sub">
            Além da mensalidade da IPCOM existem só mais duas linhas, e nenhuma delas é mensalidade
            escondida.
          </p>

          {totalPS > 0 && (
            <div className="note" style={{ borderLeftColor: 'var(--brand)', marginTop: 14 }}>
              <b>1 · A ativação, valor único e parcelável.</b> É a implantação do canal oficial: a conta
              comercial na Meta, o número verificado e os primeiros modelos de mensagem. Feita <b>uma vez</b>,
              não se repete todo mês — R$ {brl(totalPS)}, em até {parcelas}x de R$ {brl(totalPS / parcelas)}.
            </div>
          )}

          <div className="note" style={{ marginTop: 10 }}>
            <b>2 · As mensagens da Meta, cobradas pela própria Meta.</b> A maior parte do atendimento é o
            cliente que escreve primeiro{janela24hEhGratis() ? ', e responder dentro da janela de 24 horas hoje não tem custo' : ''}.
            Na ativação, o cartão da sua empresa é cadastrado direto na plataforma da Meta: é lá que essa
            conta é cobrada, <b>sem passar pela IPCOM</b>.
          </div>

          <table className="tbl" style={{ marginTop: 14 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Tipo de mensagem</th>
                <th style={{ textAlign: 'left' }}>Quando acontece</th>
                <th>Custo, definido pela Meta</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ textAlign: 'left' }}><div className="it">Resposta na janela de 24h</div></td>
                <td style={{ textAlign: 'left' }}>O cliente escreveu primeiro e você responde em até 24h: o coração do atendimento</td>
                <td className={janela24hEhGratis() ? 'd' : 'n'}>
                  {janela24hEhGratis()
                    ? `Grátis até ${fmtDate(JANELA_24H_PASSA_A_COBRAR_EM)}`
                    : meta.tarifas.servico.rotulo}
                </td>
              </tr>
              <tr>
                <td style={{ textAlign: 'left' }}><div className="it">Utilidade · aviso ou confirmação</div></td>
                <td style={{ textAlign: 'left' }}>Você inicia com um lembrete de vencimento ou um aviso de documento pronto</td>
                <td className="n">{meta.tarifas.utilidade.rotulo}</td>
              </tr>
              <tr>
                <td style={{ textAlign: 'left' }}><div className="it">Autenticação · código</div></td>
                <td style={{ textAlign: 'left' }}>Envio de código de verificação</td>
                <td className="n">{meta.tarifas.autenticacao.rotulo}</td>
              </tr>
              <tr>
                <td style={{ textAlign: 'left' }}><div className="it">Marketing · divulgação</div></td>
                <td style={{ textAlign: 'left' }}>Comunicado ou aviso geral enviado de forma ativa para a carteira</td>
                <td className="n">{meta.tarifas.marketing.rotulo}</td>
              </tr>
            </tbody>
          </table>

          <p className="cap" style={{ textAlign: 'left', marginTop: 10 }}>
            Valores por mensagem, definidos e cobrados diretamente pela Meta no cartão cadastrado pela sua
            empresa. {fraseDaFonte(meta.linhaDeReferencia)}
            {janela24hEhGratis()
              ? ` A partir de ${fmtDate(JANELA_24H_PASSA_A_COBRAR_EM)} a Meta passa a cobrar também a resposta na janela de 24h, hoje gratuita, na faixa da utilidade — é mudança dela para todo o mercado, cobrada no cartão da empresa. Avisamos com antecedência, sem letra miúda.`
              : ` A resposta na janela de 24h passou a ser cobrada em ${fmtDate(JANELA_24H_PASSA_A_COBRAR_EM)}, por decisão da Meta para todo o mercado.`}
          </p>

          <div className="note" style={{ marginTop: 12 }}>
            <b>A regra da Meta, simples de entender:</b> para <b>iniciar</b> conversa com quem não escreveu
            nas últimas 24h, usa-se uma mensagem-modelo. Quando o cliente escreve primeiro, sua equipe
            responde livremente por 24h. Os modelos a IPCOM redige e submete com você; a Meta classifica e
            aprova.
          </div>

          <Rodape texto="Valores da Meta cobrados no cartão da sua empresa, sem passar pela IPCOM" n={5} de={totalDePaginas} />
        </div>
      )}
    </div>
  );
};

export default PropostaPadrao;
